import React from 'react'
import PropTypes from 'prop-types'
import {
  CHECPLUS_MODULE_NAMES,
  NEW,
  TEST,
  DESIGN,
  RETURNED,
  ACTIVE,
  FEATURE,
  OBSOLETE,
} from 'utils/constants'
import strings from 'utils/strings'
import { reject } from 'ramda'
import { displayModuleName } from 'i18n/displayConstants'

import SearchInput from 'components/SearchV2/SearchInput'
import {
  StyledFilterItemContainer,
  StyledInputCol,
  StyledButtonCol,
  StyledButton,
  SearchFeaturesLabel,
  StyledChipTagMenu,
  StyledAutoCol,
  StyledRow,
} from './FeatureSearchBatchImportFilter.style'

const createModuleOptions = (filterModules) => {
  const moduleNames = filterModules || Object.values(CHECPLUS_MODULE_NAMES)
  return moduleNames.map((name) => ({
    key: name,
    value: name,
    title: displayModuleName(name),
  }))
}

const allStatuses = [DESIGN, TEST, ACTIVE, OBSOLETE]
const statusOptions = allStatuses.map((status) => ({
  key: status,
  value: status,
  title: status,
}))

const filtersEqualApplied = ({ moduleNames, statuses, appliedFilters }) =>
  moduleNames.length === appliedFilters.moduleNames.length &&
  statuses.length === appliedFilters.statuses.length &&
  appliedFilters.statuses.every((status) => statuses.includes(status)) &&
  appliedFilters.moduleNames.every((moduleType) =>
    moduleNames.includes(moduleType)
  )

class FeatureSearchFilters extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      ...this.defaultFilters,
      appliedFilters: { ...this.defaultFilters },
    }
  }

  defaultFilters = {
    moduleNames:
      this.props.searchFilterModules || Object.values(CHECPLUS_MODULE_NAMES),
    statuses: allStatuses,
  }
  // Filtering is a 2-step process: selecting values to filter on, then clicking the searck or "apply filters" button.
  // We track the 'applied' filters separately, so we can compare the current selection with what's been applied.
  // and disable/enable the apply & clear buttons accordingly.

  setFilters = (update) => {
    this.setState(update, () => {
      const { moduleNames, statuses } = this.state
      const filterStatuses = this.setParsedFilterStatus(statuses)
      this.props.setFilters(
        { queries: { moduleNames, statuses: filterStatuses } },
        FEATURE
      )
    })
  }

  setParsedFilterStatus = (statuses) => {
    const isDesign = (status) => status === DESIGN
    const parsedStatuses = reject(isDesign, statuses)
    const filterStatuses = statuses.includes(DESIGN)
      ? [...parsedStatuses, NEW, RETURNED]
      : [...parsedStatuses]
    return filterStatuses
  }

  selectModuleType = (value) => this.setFilters({ moduleNames: value })

  selectStatus = (statuses) => this.setFilters({ statuses })

  resetFilters = () => this.setFilters(this.defaultFilters)

  filtersEqualDefault = () =>
    this.state.moduleNames.length === this.defaultFilters.moduleNames.length &&
    this.state.statuses.length === this.defaultFilters.statuses.length

  applyFeatureFilters = () => {
    if (this.props.searchString.length > 0) {
      const { moduleNames, statuses } = this.state
      const filterStatuses = this.setParsedFilterStatus(statuses)
      this.props.applyFilters(
        { moduleNames, statuses: filterStatuses },
        FEATURE,
        this.props.searchString
      )
      this.setState({
        appliedFilters: { moduleNames, statuses },
      })
    }
  }

  componentDidMount() {
    this.setFilters(this.defaultFilters)
  }

  static getDerivedStateFromProps(props, state) {
    if (props.isSearching && !filtersEqualApplied(state)) {
      const { moduleNames, statuses } = state
      return { appliedFilters: { moduleNames, statuses } }
    }
    return null
  }

  render() {
    const {
      searchFilterModules,
      resource,
      page,
      setPage,
      searchUri,
      addToSearchRequestBody,
      appliedFilters,
      batchImport,
    } = this.props
    const moduleOptions = createModuleOptions(searchFilterModules)

    return (
      <StyledRow type="flex" align="bottom">
        <StyledInputCol span={4}>
          <SearchFeaturesLabel>
            {strings.search_features_header}
          </SearchFeaturesLabel>
          <SearchInput
            resource={resource}
            searchUri={searchUri}
            page={page}
            setPage={setPage}
            filters={appliedFilters}
            batchImport={batchImport}
            addToSearchRequestBody={addToSearchRequestBody}
            placeholder={strings.enter_search_term}
            addOnAfter
            onSearch={this.applyFeatureFilters}
          />
        </StyledInputCol>
        <StyledAutoCol>
          <StyledFilterItemContainer className="filter-item-container">
            <SearchFeaturesLabel>{strings.ecus}:</SearchFeaturesLabel>
            <StyledChipTagMenu
              value={this.state.moduleNames}
              options={moduleOptions}
              onChange={this.selectModuleType}
              placeholder={strings.search_status_required}
            />
          </StyledFilterItemContainer>
        </StyledAutoCol>
        <StyledAutoCol>
          <StyledFilterItemContainer className="filter-item-container">
            <SearchFeaturesLabel>{strings.status}:</SearchFeaturesLabel>
            <StyledChipTagMenu
              value={this.state.statuses}
              options={statusOptions}
              onChange={this.selectStatus}
              placeholder={strings.search_status_required}
            />
          </StyledFilterItemContainer>
        </StyledAutoCol>
        <StyledButtonCol span={3}>
          <StyledButton
            type="secondary"
            size="medium"
            onClick={this.resetFilters}
            text={strings.reset}
            disabled={this.filtersEqualDefault()}
          />
        </StyledButtonCol>
      </StyledRow>
    )
  }
}

FeatureSearchFilters.propTypes = {
  applyFilters: PropTypes.func,
  setFilters: PropTypes.func,
  searchString: PropTypes.string,
  isSearching: PropTypes.bool,
  resource: PropTypes.string,
  page: PropTypes.number,
  setPage: PropTypes.func,
  searchUri: PropTypes.string,
  addToSearchRequestBody: PropTypes.bool,
  appliedFilters: PropTypes.array,
  batchImport: PropTypes.bool,
  searchFilterModules: PropTypes.arrayOf(PropTypes.string),
}

export default FeatureSearchFilters
